import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../hooks/useAuth';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft, Clock, CheckCircle, Eye, Mail, Send,
  Bell, Loader2, Download, Shield, AlertCircle
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'text-slate-400', viewed: 'text-amber-400',
  signed: 'text-emerald-400', declined: 'text-red-400'
};

const EVENT_META = {
  email_sent:        { label: 'Email sent',        icon: Mail,         color: 'text-blue-400',    bg: 'bg-blue-500/10' },
  email_opened:      { label: 'Email opened',      icon: Eye,          color: 'text-indigo-400',  bg: 'bg-indigo-500/10' },
  link_clicked:      { label: 'Link clicked',      icon: Send,         color: 'text-purple-400',  bg: 'bg-purple-500/10' },
  document_viewed:   { label: 'Document viewed',   icon: Eye,          color: 'text-amber-400',   bg: 'bg-amber-500/10' },
  document_signed:   { label: 'Signed',            icon: CheckCircle,  color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  document_completed:{ label: 'All signed',        icon: CheckCircle,  color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  reminder_sent:     { label: 'Reminder sent',     icon: Bell,         color: 'text-amber-400',   bg: 'bg-amber-500/10' },
};

function TimelineEvent({ event }) {
  const meta = EVENT_META[event.event_type] || { label: event.event_type, icon: Clock, color: 'text-slate-400', bg: 'bg-slate-800' };
  const Icon = meta.icon;
  return (
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg flex-shrink-0 ${meta.bg}`}>
        <Icon size={14} className={meta.color} />
      </div>
      <div className="flex-1 min-w-0 pb-4 border-b border-slate-800/50">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm font-medium ${meta.color}`}>{meta.label}</span>
          <span className="text-xs text-slate-600 whitespace-nowrap">
            {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
          </span>
        </div>
        {event.recipient_name && (
          <div className="text-xs text-slate-500 mt-0.5">{event.recipient_name}</div>
        )}
        {event.ip_address && (
          <div className="text-xs text-slate-600 mt-0.5">IP: {event.ip_address}</div>
        )}
      </div>
    </div>
  );
}

export default function DocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [voiding, setVoiding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setDoc(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const sendReminder = async () => {
    setSending(true);
    try {
      await api.post(`/documents/${id}/remind`);
      alert('Reminder sent to pending signers');
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    } finally {
      setSending(false);
    }
  };

  const voidDocument = async () => {
    if (!window.confirm('Void this document? This cannot be undone.')) return;
    setVoiding(true);
    try {
      await api.delete(`/documents/${id}`);
      navigate('/documents');
    } catch (e) {
      alert('Failed to void');
    } finally {
      setVoiding(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!doc) return (
    <div className="p-6 text-slate-400 text-center">Document not found</div>
  );

  const signers = doc.recipients?.filter(r => r.role === 'signer') || [];
  const cc = doc.recipients?.filter(r => r.role === 'cc') || [];
  const signedCount = signers.filter(r => r.status === 'signed').length;
  const progress = signers.length > 0 ? (signedCount / signers.length) * 100 : 0;

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-colors"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main */}
        <div className="xl:col-span-2 space-y-6">
          {/* Header card */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">{doc.title}</h1>
                <p className="text-slate-500 text-sm mt-1">
                  {doc.sending_company_name} · Sent by {doc.sender_name}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {['sent','partially_signed'].includes(doc.status) && (
                  <button
                    onClick={sendReminder}
                    disabled={sending}
                    className="flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 text-sm font-semibold px-4 py-2 rounded-lg transition-colors border border-amber-500/30"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
                    Remind
                  </button>
                )}
                {doc.signed_file_path && (
                  <a
                    href={`/uploads/signed/signed-${id}.pdf`}
                    download
                    className="flex items-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 text-sm font-semibold px-4 py-2 rounded-lg transition-colors border border-emerald-500/30"
                  >
                    <Download size={14} /> Download
                  </a>
                )}
                {!['completed','voided','expired'].includes(doc.status) && (
                  <button
                    onClick={voidDocument}
                    disabled={voiding}
                    className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg transition-colors border border-red-500/20"
                  >
                    Void
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-5">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-400">Signature Progress</span>
                <span className="text-white font-semibold">{signedCount} / {signers.length} signed</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-800">
              {[
                { label: 'Status', value: doc.status?.replace(/_/g, ' ') },
                { label: 'Sent', value: doc.sent_at ? format(new Date(doc.sent_at), 'MMM d, yyyy') : '—' },
                { label: 'Expires', value: doc.expires_at ? format(new Date(doc.expires_at), 'MMM d, yyyy') : '—' },
                { label: 'Completed', value: doc.completed_at ? format(new Date(doc.completed_at), 'MMM d, yyyy') : '—' },
              ].map(item => (
                <div key={item.label}>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{item.label}</div>
                  <div className="text-sm font-semibold text-white capitalize">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800">
              <h2 className="font-semibold text-white">Recipients</h2>
            </div>
            <div className="divide-y divide-slate-800/50">
              {signers.map(r => (
                <div key={r.id} className="px-6 py-4 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold text-slate-300 flex-shrink-0">
                    {r.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.email}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold capitalize ${STATUS_COLORS[r.status] || 'text-slate-400'}`}>
                      {r.status}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {r.signed_at ? format(new Date(r.signed_at), 'MMM d, h:mm a') : ''}
                      {r.document_viewed_at && r.status !== 'signed' ? '👁 Viewed' : ''}
                    </div>
                  </div>
                </div>
              ))}
              {cc.map(r => (
                <div key={r.id} className="px-6 py-4 flex items-center gap-4 opacity-60">
                  <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold text-slate-500 flex-shrink-0">
                    {r.name[0]}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-400">{r.name}</div>
                    <div className="text-xs text-slate-600">{r.email} · CC</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar — Timeline */}
        <div className="space-y-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="font-semibold text-white mb-4">Activity Timeline</h2>
            {doc.activity?.length === 0 ? (
              <p className="text-slate-500 text-sm">No activity yet</p>
            ) : (
              <div className="space-y-1">
                {(doc.activity || []).map(event => (
                  <TimelineEvent key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>

          {/* Security info */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={15} className="text-blue-400" />
              <span className="text-sm font-semibold text-white">Security</span>
            </div>
            <div className="space-y-2 text-xs text-slate-500">
              <div>Hash: <span className="text-slate-400 font-mono break-all">{doc.file_hash?.slice(0, 16)}...</span></div>
              <div>Unique tokens per recipient</div>
              <div>IP + timestamp captured on signature</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
